import { Button, Card, Col, Form, HeaderRow, LoaderOverlay, Row } from "@emanprague/ppaskit";
import Tooltip from "@emanprague/ppaskit/dist/Tooltip";
import { Check, Input, Select } from "@frui.ts/bootstrap";
import { getValidationMessage, hasVisibleErrors } from "@frui.ts/validation";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { ConditionalTooltip } from "components/ConditionalTooltip";
import SupplyPointSelect from "controls/twoway/SupplyPointSelect";
import { addressToHuman } from "helpers/utils";
import { observer } from "mobx-react-lite";
import React from "react";
import iconInfo from "@emanprague/ppaskit/dist/assets/icons/Info.svg";
import EtsDeclarationFormViewModel from "viewModels/requests/etsDeclarationFormViewModel";
import styles from "./etsFormStyle.module.scss";

export const etsDeclarationFormView: ViewComponent<EtsDeclarationFormViewModel> = observer(({ vm }) => {
  const tr = vm.translate;

  const checkLabel = (key: string) => (
    <div className={styles.checkboxLabel}>
      {tr(key)}
      <Tooltip id={`${key}_tooltip`} placement="top" tooltip={tr(`${key}_tooltip`)}>
        <img draggable={false} src={iconInfo} width={22} height={22} alt={tr(`${key}_tooltip`)} />
      </Tooltip>
    </div>
  );

  return (
    <>
      <LoaderOverlay loading={vm.busyWatcher.isBusy} text={tr("sending")}>
        <Row className="mb-3 mb-md-5 align-items-center mx-2 mx-sm-0">
          <Col className="order-first">
            <h1 id="etsDeclarationTitle" className="display-2 mb-0">
              {tr("title")}
            </h1>
          </Col>
          <Col className="d-flex justify-content-end order-1 order-md-last">
            <Button
              aria-describedby="etsDeclarationTitle"
              variant="secondary"
              disabled={vm.busyWatcher.isBusy}
              onClick={vm.requestClose}>
              {tr("close")}
            </Button>
          </Col>
        </Row>
        <Row noGutters>
          <Col xs={12} md={6} className="pr-xs-2">
            <Card>
              <Card.Body>
                <HeaderRow title={tr("basic_information")} level={2} className="text-center" size="display-4" />
                <table className="table aligned wider-row">
                  <tbody>
                    <tr>
                      <th className="border-top-0">{tr("customer")}</th>
                      <td align="right" className="border-top-0">
                        {vm.activePartnerFullName}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">{tr("address")}</th>
                      <td align="right">{addressToHuman(vm.partner?.permanentAddress)}</td>
                    </tr>
                    <tr>
                      <th scope="row">{tr("cin")}</th>
                      <td align="right">{vm.partner?.orgId}</td>
                    </tr>
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6} className="pt-3 pl-md-2 pt-md-0">
            <Card>
              <Card.Body>
                <HeaderRow title={tr("authorized_person.title")} level={2} className="text-center" size="display-4" />
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>{tr("authorized_person.first_name")}</Form.Label>
                      <Input target={vm.data} property="firstName" />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>{tr("authorized_person.last_name")}</Form.Label>
                      <Input target={vm.data} property="lastName" />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group>
                  <Form.Label>{tr("authorized_person.position")}</Form.Label>
                  <Input target={vm.data} property="position" />
                </Form.Group>
                <div className={styles.authorizedPersonContainer}>
                  <Check custom target={vm.data} property="authorizedPerson" label={checkLabel("authorized_person_check")} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row noGutters>
          <Col>
            <Card>
              <Card.Body>
                <Check
                  custom
                  target={vm.data}
                  property="lowConsumption"
                  label={checkLabel("low_consumption")}
                  onValueChanged={vm.toggleLowConsumption}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row noGutters>
          <Col>
            <Card>
              <Card.Body>
                <HeaderRow title={tr("supply_points")} level={2} className="text-center" size="display-4" />
                {vm.availableYears.length === 0 ? (
                  <span
                    className={`${styles.noSupplyPointsMessage} invalid-feedback`}
                    dangerouslySetInnerHTML={{ __html: tr("no_supply_points_available") }}></span>
                ) : (
                  <Row className="mb-3">
                    <Col className={styles.yearContainer}>
                      <Form.Group className="mb-0">
                        <Form.Label>{tr("year")}</Form.Label>
                        <Select
                          items={vm.availableYears}
                          target={vm.data}
                          property="year"
                          mode="key"
                          keyProperty="id"
                          textProperty="label"
                          emptyText={tr("select_year")}
                          onValueChanged={vm.deselectSupplyPoints}
                        />
                      </Form.Group>
                    </Col>
                    <Col className={styles.supplyPointControls} style={{ alignItems: vm.data.year ? "end" : "center" }}>
                      {vm.data.year ? (
                        <>
                          {!vm.data.lowConsumption && (
                            <div className="d-flex justify-content-end">
                              <Button variant="outline-danger" onClick={vm.deselectSupplyPoints}>
                                {tr("deselect_all")}
                              </Button>
                              <Button variant="outline-success" onClick={vm.selectAllSupplyPoints}>
                                {tr("select_all")}
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <span>{tr("select_year_first")}</span>
                      )}
                    </Col>
                  </Row>
                )}
                {!!vm.data.year && (
                  <>
                    <SupplyPointSelect
                      multiselect={!vm.data.lowConsumption}
                      supplyPoints={vm.supplyPointsWithIcons}
                      target={vm.data}
                      property="supplyPointIds"
                      translateFunc={vm.translateGeneral}
                    />
                    <div className="invalid-feedback">{getValidationMessage(vm.data, "supplyPointIds")}</div>
                  </>
                )}
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <HeaderRow title={tr("statement.title")} level={2} className="text-center" size="display-4" />
                <p>{tr("statement.subtext")}</p>
                <p>{tr("statement.pretext")}</p>

                <div className={styles.checkboxContainer}>
                  <Check custom target={vm.data} property="farmer" label={checkLabel("farmer")} />
                  <Check custom target={vm.data} property="forester" label={checkLabel("forester")} />
                  {!vm.data.lowConsumption ? (
                    <span>{tr("statement.farmer_or_forester")}</span>
                  ) : (
                    <span>{tr("statement.low_consumption")}</span>
                  )}
                </div>
              </Card.Body>
            </Card>
            <div className={styles.footer}>
              {vm.errorMessage && <div className={styles.errorMessage}>{vm.errorMessage}</div>}
              <div className={styles.submitContainer}>
                <span className={styles.submitInfo}>{tr("submit_info")}</span>
                <ConditionalTooltip
                  condition={!!getValidationMessage(vm.data, "supplyPoints")}
                  tooltip={tr("select_supply_point")}
                  placement="left"
                  id="submit_tooltip">
                  <Button
                    aria-describedby="etsDeclarationTitle"
                    variant="primary"
                    onClick={vm.sendRequest}
                    disabled={hasVisibleErrors(vm.data)}>
                    {tr("submit")}
                  </Button>
                </ConditionalTooltip>
              </div>
            </div>
          </Col>
        </Row>
      </LoaderOverlay>
    </>
  );
});

registerView(etsDeclarationFormView, EtsDeclarationFormViewModel);
